import type { FolderDto, FolderPathSegmentDto, AncestryResponseDto, FolderPathCreationDto, AddToFolderDto, GetFolderContentResponseDto, GetFolderContentScrollQueryParams, UpdateFolderAccessControlDto, GetFolderContentQueryParams } from '@mimir/common/dto/folderModels';
import type { SearchScrollResponse } from '@mimir/common/dto/searchResultHit';
import type { HttpQueryParameters, AdditionalParams } from '@mimir/common/util/apiClient';
import { apiPut, apiDelete, apiPost, apiGet } from '@mimir/common/util/http';
import { invariant } from '@mimir/common/util/invariant';
import { ofCreateFolderPath, ofFolder, ofFolderAncestry, ofFolderContent, ofFolderSafe, ofFolders, ofItemAncestry } from '@mimir/common/aws/api/path';
import type { TaskPriority } from '@mimir/common/async/taskPriority';
import type { BackendClient } from '@mimir/common/util/backendClient';
import { assumeResultObject, assumeResultArray, vagueResult } from '@mimir/common/client/assumeResult';
import { injectDefaultQueryParamsForItemApis } from './injectItemQueryParams';
export const api_getItemAncestry = async (c: BackendClient, itemId: string,
  storageManagerVersion: string | undefined, priority?: TaskPriority): Promise<FolderPathSegmentDto[][]> => {
  const params: AdditionalParams = {};
  params.priority = priority;
  if (storageManagerVersion !== undefined) {
    params.headers = {
      strMgrVersion: storageManagerVersion
    };
  }
  return apiGet(c.getApiClient(), ofItemAncestry(c.getServiceLinks(), itemId), params).then(result => assumeResultArray<FolderPathSegmentDto[]>(result));
};

/************************ folders ************************/

export const api_getFolderContent = (c: BackendClient, folderId: string, queryParams?: GetFolderContentQueryParams): Promise<GetFolderContentResponseDto> =>
  apiGet(c.getApiClient(), ofFolderContent(c.getServiceLinks(), folderId), injectDefaultQueryParamsForItemApis({
    queryParams
  })).then(result => vagueResult(result));

export const api_getFolderContentScroll = (c: BackendClient, folderId: string, queryParams: GetFolderContentScrollQueryParams): Promise<SearchScrollResponse> =>
  apiGet(c.getApiClient(), ofFolderContent(c.getServiceLinks(), folderId), injectDefaultQueryParamsForItemApis({
    queryParams
  })).then(result => assumeResultObject<SearchScrollResponse>(result, 'items'));

export const api_createFolder = async (c: BackendClient, folderName: string, parentFolderId: string, isHidden: boolean): Promise<FolderDto> =>
  apiPost(c.getApiClient(), ofFolders(c.getServiceLinks()), {
    name: folderName,
    parent: parentFolderId,
    isHidden,
  }).then(result => assumeResultObject<FolderDto>(result, 'hasSubfolder'));


export const api_renameFolder = async (c: BackendClient, folderId: string, folderName: string) =>
  apiPost(c.getApiClient(), ofFolder(c.getServiceLinks(), folderId), {
    name: folderName,
  });

export const api_updateFolderSortKeyAndParent = async (
  c: BackendClient,
  params: {
    folderId: string;
    sortKey: string | null;
    targetFolderId: string | undefined;
  },
  queryParams: HttpQueryParameters = {},
) => {
  const { folderId, sortKey, targetFolderId } = params;
  return apiPost(
    c.getApiClient(),
    ofFolder(c.getServiceLinks(), folderId),
    {
      parentId: targetFolderId,
      sortKey,
    },
    { queryParams }
  );
};

/**
 * Move a folder to a new parent folder.
 *
 * Note: this method will always reset the sort key of the folder. (sortKey: null)
 */
export const api_moveFolder = async (c: BackendClient, folderId: string, targetFolderId: string, queryParams: HttpQueryParameters = {}) =>
  api_updateFolderSortKeyAndParent(c, { folderId, targetFolderId, sortKey: null }, queryParams);

export const api_updateFolderAccessControl = async (c: BackendClient, folderId: string, accessControl: UpdateFolderAccessControlDto, queryParams: HttpQueryParameters = {}) =>
  apiPost(c.getApiClient(), ofFolder(c.getServiceLinks(), folderId), accessControl, { queryParams });

export const api_updateFolderOwner = async (c: BackendClient, folderId: string, newOwner: string, queryParams: HttpQueryParameters = {}) =>
  apiPost(c.getApiClient(), ofFolder(c.getServiceLinks(), folderId), { owner: newOwner }, { queryParams });


export const api_deleteFolder = async (c: BackendClient, folderId: string) =>
  apiDelete(c.getApiClient(), ofFolder(c.getServiceLinks(), folderId));

export const api_deleteFolderSafe = async (c: BackendClient, folderId: string, options: AdditionalParams = {}) =>
  apiDelete(c.getApiClient(), ofFolderSafe(c.getServiceLinks(), folderId), {}, options);


export const api_addItemToFolder = async (c: BackendClient, folderId: string, itemId: string, parentIdToRemove?: string) =>
  apiPut(c.getApiClient(), ofFolderContent(c.getServiceLinks(), folderId), { id: itemId, parentId: parentIdToRemove } as AddToFolderDto);


export const api_removeItemFromFolder = async (c: BackendClient, folderId: string, itemId: string) =>
  apiDelete(c.getApiClient(), ofFolderContent(c.getServiceLinks(), folderId), {}, { queryParams: { id: itemId } });


export const api_getFolderAncestry = (c: BackendClient, ids: string[], type: 'folder' | 'item'): Promise<AncestryResponseDto> => {
  invariant(ids.length > 0, 'Should not make meaningless getFolderAncestry call');
  return apiPost(c.getApiClient(), ofFolderAncestry(c.getServiceLinks()), {
    ids,
    type
  }).then(result => assumeResultObject<AncestryResponseDto>(result));
};


export const api_createFolderPath = async (c: BackendClient, createPathRequest: FolderPathCreationDto): Promise<Record<string, string>[]> =>
  apiPost(c.getApiClient(), ofCreateFolderPath(c.getServiceLinks()), createPathRequest)
    .then(result => assumeResultArray<Record<string, string>>(result));

